import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { tokens, fonts } from '../theme';
import site, { usePageMeta } from '../content';
import Faq from './Faq';

// One page per file type (PowerPoint, PDF, Word), all driven by site.json.
const FormatPage = ({ format }) => {
  usePageMeta(format);
  const others = site.formats.filter((f) => f.path !== format.path);

  return (
    <>
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <Box component={RouterLink} to="/" sx={{ color: 'inherit' }}>{site.siteName}</Box>
          {' / '}
          {format.name}
        </Typography>
        <Typography variant="h1" component="h1">{format.h1}</Typography>
        <Typography id="summary" color="text.secondary" sx={{ mt: 2.5, fontSize: '1.125rem', maxWidth: 680 }}>
          {format.intro}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Last updated: {format.updated}
        </Typography>
        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Button component={RouterLink} to="/upload" variant="contained" size="large" startIcon={<UploadFileIcon />}>
            Replace a logo in your {format.name} file
          </Button>
          <Typography variant="body2" color="text.secondary">
            Free, no sign-up. Up to {site.maxFileSizeMb} MB.
          </Typography>
        </Box>
      </Container>

      <Box component="section" id="how-it-works" aria-labelledby="steps-title" sx={{ borderTop: `1px solid ${tokens.line}`, py: { xs: 7, md: 9 } }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" id="steps-title" sx={{ mb: 4 }}>{format.stepsTitle}</Typography>
          <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 3 }}>
            {format.steps.map((step, i) => (
              <Box component="li" key={step.title} sx={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: 2 }}>
                <Typography sx={{ fontFamily: fonts.display, fontWeight: 700, color: tokens.accent, fontSize: '1.25rem' }}>
                  {i + 1}
                </Typography>
                <Box>
                  <Typography variant="h3" component="h3" sx={{ mb: 0.5 }}>{step.title}</Typography>
                  <Typography color="text.secondary">{step.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {format.sections.map((section, n) => (
        <Box component="section" key={section.heading} aria-labelledby={`section-${n}`} sx={{ borderTop: `1px solid ${tokens.line}`, py: { xs: 7, md: 9 } }}>
          <Container maxWidth="md">
            <Typography variant="h2" component="h2" id={`section-${n}`} sx={{ mb: 3 }}>{section.heading}</Typography>
            {section.paragraphs.map((p) => (
              <Typography key={p} color="text.secondary" sx={{ mb: 2, maxWidth: 680 }}>{p}</Typography>
            ))}
            {section.list && (
              <Box component="ol" sx={{ pl: 3, mt: 0, mb: 2, maxWidth: 680, color: 'text.secondary', display: 'grid', gap: 1 }}>
                {section.list.map((item) => <Typography component="li" key={item}>{item}</Typography>)}
              </Box>
            )}
            {section.after && (
              <Typography color="text.secondary" sx={{ maxWidth: 680 }}>{section.after}</Typography>
            )}
          </Container>
        </Box>
      ))}

      <Box component="section" aria-labelledby="faq-title" sx={{ borderTop: `1px solid ${tokens.line}`, py: { xs: 7, md: 9 } }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" id="faq-title" sx={{ mb: 4 }}>
            Questions about {format.name} files
          </Typography>
          <Faq items={format.faq} />
        </Container>
      </Box>

      <Box component="section" sx={{ borderTop: `1px solid ${tokens.line}`, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" sx={{ mb: 2 }}>Other file types</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {others.map((f) => (
              <Box key={f.path} component={RouterLink} to={f.path} sx={{ color: tokens.accentStrong, fontWeight: 600 }}>
                {f.h1}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default FormatPage;
